// Escaneo continuo de bultos para cargar al camión (agencia / camión / gira).
// El encargado escanea TODOS los bultos seguidos sin entrar a cada pedido:
//  - la app detecta sola a qué pedido pertenece cada QR (formato "pedidoId|n/total")
//  - suena un beep corto por cada bulto escaneado
//  - suena un "ding" distinto cuando se completa un pedido → se marca CARGADO
//  - muestra el progreso en vivo de todos los pedidos

import React, { useRef, useState, useCallback, useMemo } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import QrScanner from "../../../components/QrScanner";

// ── Sonidos (Web Audio, sin archivos) ──────────────────────────
let _audioCtx = null;
function ctx() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    return _audioCtx;
  } catch { return null; }
}
function tono(freqs, dur = 0.12, vol = 0.2, type = "sine") {
  const c = ctx(); if (!c) return;
  const o = c.createOscillator(); const g = c.createGain();
  o.connect(g); g.connect(c.destination); o.type = type;
  freqs.forEach((f, i) => o.frequency.setValueAtTime(f, c.currentTime + i * (dur / freqs.length)));
  g.gain.setValueAtTime(vol, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
  o.start(); o.stop(c.currentTime + dur);
}
const playBulto    = () => tono([1100], 0.07, 0.18);                 // beep corto
const playCompleto = () => tono([880, 1318.5], 0.28, 0.2);           // ding ascendente
const playError    = () => tono([300, 200], 0.22, 0.22, "square");   // buzz grave

export default function EscaneoContinuoModal({ pedidos, collection: col = "pedidos_despachados", onClose }) {
  // pedidos: [{ id, numero, cliente, bultos, agencia? }]
  const pedidosMap = useMemo(() => Object.fromEntries(pedidos.map(p => [p.id, p])), [pedidos]);

  // refs = estado siempre actual (el scanner dispara muy seguido)
  const scannedRef    = useRef({});          // { pedidoId: Set<bultoN> }
  const completadosRef = useRef(new Set());  // pedidoIds ya marcados CARGADO

  const [, force] = useState(0);
  const rerender = () => force(n => n + 1);
  const [ultimo, setUltimo] = useState(null); // feedback del último escaneo
  const [error, setError]   = useState("");

  async function marcarCargado(pid, bultos) {
    try {
      await updateDoc(doc(db, col, pid), {
        cargaEstado: "CARGADO",
        cargadoAt: serverTimestamp(),
        bultosEscaneados: [...bultos].sort((a, b) => a - b),
      });
    } catch (e) {
      console.error("[EscaneoContinuo] no se pudo marcar CARGADO", pid, e);
      setError("Error al guardar la carga de #" + pid);
    }
  }

  const handleScan = useCallback((raw) => {
    const texto = typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw);
    const m = texto.match(/^(.+)\|(\d+)\/(\d+)$/);
    if (!m) return; // en modo continuo ignoramos QR que no son de bulto
    const pid = m[1];
    const bultoN = Number(m[2]);

    const pedido = pedidosMap[pid];
    if (!pedido) { setError(`#${pid} no pertenece a esta carga`); playError(); rerender(); return; }

    if (!scannedRef.current[pid]) scannedRef.current[pid] = new Set();
    const set = scannedRef.current[pid];
    if (set.has(bultoN)) return; // ya escaneado → ignorar (sin re-beep)

    set.add(bultoN);
    setError("");
    const total = Math.max(1, Number(pedido.bultos) || 1);

    if (set.size >= total && !completadosRef.current.has(pid)) {
      completadosRef.current.add(pid);
      marcarCargado(pid, set);
      playCompleto();
      setUltimo({ pid, numero: pedido.numero || pid, completo: true, size: set.size, total });
    } else {
      playBulto();
      setUltimo({ pid, numero: pedido.numero || pid, completo: false, size: set.size, total });
    }
    rerender();
  }, [pedidosMap]);

  // Ordenar para la lista: incompletos primero, después completados
  const lista = useMemo(() => {
    const arr = [...pedidos];
    return arr.sort((a, b) => {
      const ca = completadosRef.current.has(a.id) ? 1 : 0;
      const cb = completadosRef.current.has(b.id) ? 1 : 0;
      return ca - cb;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidos, ultimo]);

  const totalPedidos    = pedidos.length;
  const totalCompletos  = completadosRef.current.size;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0f172a", zIndex: 1000, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #1e293b" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 800, color: "#fff" }}>📷 Escaneo continuo</h3>
          <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#94a3b8" }}>
            {totalCompletos}/{totalPedidos} pedidos cargados
          </p>
        </div>
        <button onClick={onClose} style={{ background: "#1e293b", border: "none", color: "#fff", borderRadius: "10px", padding: "8px 14px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
          Terminar
        </button>
      </div>

      {/* Cámara */}
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />
        {/* Feedback último escaneo */}
        <div style={{ marginTop: "8px", minHeight: "24px", textAlign: "center" }}>
          {error ? (
            <span style={{ color: "#fca5a5", fontSize: "13px", fontWeight: 700 }}>⚠️ {error}</span>
          ) : ultimo ? (
            <span style={{ color: ultimo.completo ? "#86efac" : "#bae6fd", fontSize: "13px", fontWeight: 700 }}>
              {ultimo.completo ? `✅ #${ultimo.numero} completo (${ultimo.total} bultos)` : `📦 #${ultimo.numero} — ${ultimo.size}/${ultimo.total}`}
            </span>
          ) : (
            <span style={{ color: "#64748b", fontSize: "13px" }}>Apuntá a las etiquetas, una tras otra…</span>
          )}
        </div>
      </div>

      {/* Lista de pedidos con progreso */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
        {lista.map(p => {
          const set = scannedRef.current[p.id];
          const size = set ? set.size : 0;
          const total = Math.max(1, Number(p.bultos) || 1);
          const completo = completadosRef.current.has(p.id);
          return (
            <div key={p.id} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: completo ? "#14532d" : size > 0 ? "#1e3a5f" : "#1e293b",
              border: `1px solid ${completo ? "#16a34a" : size > 0 ? "#2563eb" : "#334155"}`,
              borderRadius: "10px", padding: "10px 14px",
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
                  {completo ? "✅ " : ""}{p.numero || p.id}
                  {p.agencia ? <span style={{ color: "#93c5fd", fontWeight: 600 }}> · {p.agencia}</span> : ""}
                </div>
                <div style={{ fontSize: "12px", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.cliente}</div>
              </div>
              <span style={{
                flexShrink: 0, fontSize: "13px", fontWeight: 800,
                color: completo ? "#86efac" : "#bae6fd",
              }}>
                {size}/{total}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
